import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FormInput, Wrapper } from 'components'
import { AuthContext } from 'contexts/AuthContext'
import { Rocket } from 'components/Icons/rocket'
import { downloadPDF } from 'utils/downloadPdf'
import {
  Button,
  Card,
  Modal,
  ModalContent,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  toast,
} from 'keep-react'
import { ArrowLeft, DownloadSimple, MapPin } from 'phosphor-react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { PolicyListItem, PolicyListResponse } from 'api/types/policies'
import { policiesService } from 'api/services/policies'
import { RouteNamesAuth } from 'routes/enum'
import { docService } from 'api/services/doc'
import { formatCurrency } from 'utils/currency'
import { renderBadge } from 'utils/renderBadge'
import { isoCountries } from 'utils/countries'
import { AppContext } from 'contexts/AppContext'

const validationSchema = Yup.object().shape({
  ADDRESS1: Yup.string().required('Obrigatório'),
  ADDRESS3: Yup.string().required('Obrigatório'),
  ADDRESS4: Yup.string(),
  POSTCODE: Yup.string()
    .required('Obrigatório')
    .matches(/^\d{4}-\d{3}$/, 'Código Postal deve ser no formato ####-###'),
})

type InitialValues = {
  ADDRESS1?: string
  ADDRESS2?: string
  ADDRESS3?: string
  ADDRESS4?: string
  POSTCODE?: string
  COUNTRY?: string
}

export const Policy = () => {
  const { policyNo } = useParams() as unknown as { policyNo: number }
  const [policy, setPolicy] = useState<PolicyListItem | null>(null)
  const navigate = useNavigate()
  const { currentBroker, accessLevel } = useContext(AuthContext)
  const { setIsCallingApi } = useContext(AppContext)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [changeAddress, setChangeAddress] = useState(false)
  const [initialValues, setInitialValues] = useState<InitialValues>({
    ADDRESS1: '',
    ADDRESS2: '',
    ADDRESS3: '',
    ADDRESS4: '',
    POSTCODE: '',
    COUNTRY: '',
  })
  const brokerNo = currentBroker?.BrokerNo ? currentBroker?.BrokerNo : 0

  //get policy details
  useEffect(() => {
    const fetchPolicy = async () => {
      if (!policyNo || brokerNo === 0) return false

      try {
        const response = (await policiesService.getPoliciesList({
          policyNo: Number(policyNo),
          brokerNo,
          page: 0,
        })) as PolicyListResponse
        if (Array.isArray(response.Policies)) {
          setPolicy(response.Policies[0])
          setInitialValues({
            ADDRESS1: response.Policies[0]?.AddressLine1 || '',
            ADDRESS2: response.Policies[0]?.AddressLine2 || '',
            ADDRESS3: response.Policies[0]?.AddressLine3 || '',
            ADDRESS4: response.Policies[0]?.AddressLine4 || '',
            POSTCODE: response.Policies[0]?.Postcode || '',
            COUNTRY:
              isoCountries.find(
                country =>
                  country.code ===
                  response.Policies[0]?.Country.toLocaleUpperCase(),
              )?.country ||
              response.Policies[0]?.Country ||
              '',
          })

          setChangeAddress(false)
          setIsLoading(false)
        } else {
          setPolicy(null)
          console.error('Policy error:', response)
        }
      } catch (error) {
        // setTableData(null)
      }
    }
    fetchPolicy()
  }, [policyNo, brokerNo])

  // download receipt document
  const getReceipt = async () => {
    setIsCallingApi(true)
    try {
      const response = await docService.getDocumentList(policyNo, 0, 'I')
      if (!response.Documents || response.Documents.length === 0) {
        toast.error('Nenhum documento encontrado')
        return
      }
      downloadPDF({
        base64Data: response.Documents[0].Document,
        filename: `Aviso de Recibo - ${policyNo}.pdf`,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsCallingApi(false)
    }
  }

  // download invoice document
  const getInvoice = async () => {
    setIsCallingApi(true)

    try {
      const response = await docService.getDocumentList(policyNo, 0, 'R')
      if (!response.Documents || response.Documents.length === 0) {
        toast.error('Nenhum documento encontrado')
        return
      }
      downloadPDF({
        base64Data: response.Documents[0].Document,
        filename: `Factura Recibo - ${policyNo}.pdf`,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsCallingApi(false)
    }
  }

  // download private conditions document
  const getPrivateConditions = async () => {
    setIsCallingApi(true)
    try {
      const response = await docService.getDocumentList(policyNo, 0)
      if (!response.Documents || response.Documents.length === 0) {
        toast.error('Nenhum documento encontrado')
        return
      }
      downloadPDF({
        base64Data: response.Documents[0].Document,
        filename: `Condicoes Particulares - ${policyNo}.pdf`,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsCallingApi(false)
    }
  }

  // toggle address modal
  const updateAddress = () => {
    setChangeAddress(!changeAddress)
  }

  // define action buttons
  const buttonArea = [
    {
      label: 'Aviso de Recibo',
      action: () => getReceipt(),
      icon: <DownloadSimple size={16} />,
    },
    {
      label: 'Factura Recibo',
      action: () => getInvoice(),
      icon: <DownloadSimple size={16} />,
    },
    {
      // label: 'Condições Particulares',
      label: 'Condições Contratuais',
      action: () => getPrivateConditions(),
      icon: <DownloadSimple size={16} />,
    },
  ]

  // formik for address change
  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      const updateAddressBody = {
        policyAddress: Object.entries(values)
          .map(([k, v]) => ({
            question: k,
            answer: v,
            inputId: k,
          }))
          .map(item => {
            if (item.question === 'COUNTRY') {
              return {
                ...item,
                answer:
                  isoCountries.find(
                    country =>
                      country.country === item.answer ||
                      country.code === item.answer ||
                      country.code === item.answer,
                  )?.code || item.answer,
              }
            }
            return item
          }),
      }

      setIsCallingApi(true)
      try {
        const response = (await policiesService.updatePolicyAddress({
          policyNo,
          body: updateAddressBody,
        })) as PolicyListResponse
        setPolicy(response.Policies[0])
        setInitialValues({
          ADDRESS1: response.Policies[0]?.AddressLine1 || '',
          ADDRESS2: response.Policies[0]?.AddressLine2 || '',
          ADDRESS3: response.Policies[0]?.AddressLine3 || '',
          ADDRESS4: response.Policies[0]?.AddressLine4 || '',
          POSTCODE: response.Policies[0]?.Postcode || '',
          COUNTRY:
            isoCountries.find(
              country =>
                country.code ===
                response.Policies[0]?.Country.toLocaleUpperCase(),
            )?.country ||
            response.Policies[0]?.Country ||
            '',
        })
        setChangeAddress(false)
      } catch (error) {
        console.error(error)
      } finally {
        setIsCallingApi(false)
      }
    },
  })

  useEffect(() => {
    if (accessLevel !== null && accessLevel < 1) {
      navigate(RouteNamesAuth.NO_PERMISSION)
    }
  }, [accessLevel, navigate])

  if (!policy) {
    return <Spinner />
  }

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <Button
          variant="outline"
          color="secondary"
          className="mb-6"
          onClick={() => navigate(RouteNamesAuth.POLICIES)}
        >
          <ArrowLeft size={16} /> Apólices
        </Button>
        <div className="flex items-start gap-2 w-full">
          <div
            className={`${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'} flex items-center flex-col gap-1 pt-3 -translate-y-1.5`}
          >
            <Rocket />
          </div>
          <h2 className="text-body-1 uppercase pb-10">
            {(policy && policy['Produto']) || 'Produto indisponível'}
          </h2>
        </div>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full bg-metal-25">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : !policy ? (
          <Card className="p-4 w-full max-w-full">
            <div className="text-heading-6 text-metal-400">
              Apólice indisponível
            </div>
          </Card>
        ) : (
          <div>
            <Card className="p-4 w-full max-w-full mb-4 bg-metal-25">
              <div className="p-4">
                <h2
                  className={`text-body-1 text-primary-600 ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'}`}
                >
                  Dados do Tomador
                </h2>

                <Table className="pb-10 bg-metal-25">
                  <TableBody>
                    <TableRow>
                      <TableHead className="!h-10 bg-metal-25  font-bold">
                        Tomador
                      </TableHead>
                      <TableCell className=" normal-case">
                        {policy.Tomador || 'Tomador indisponível'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableHead className="!h-10 bg-metal-25 font-bold">
                        Nº da Apólice
                      </TableHead>
                      <TableCell className=" normal-case">
                        {policy['Nº da Apólice'] ||
                          'Nº da Apólice indisponível'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableHead className="!h-10 bg-metal-25 font-bold">
                        Morada
                      </TableHead>
                      <TableCell className=" normal-case">
                        {policy.AddressLine1}
                        {policy.AddressLine2 && (
                          <>
                            {policy.AddressLine2} <br />
                          </>
                        )}
                        <br />
                        {policy.Postcode} {policy.AddressLine3}
                        <br />
                        {
                          isoCountries.find(
                            country =>
                              country.code ===
                              policy.Country.toLocaleUpperCase(),
                          )?.country
                        }
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableHead className="!h-10 bg-metal-25 font-bold">
                        NIF
                      </TableHead>
                      <TableCell className=" normal-case">
                        {policy.NIF || 'NIF indisponível'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <div className="flex mt-6">
                  <div>
                    <Button
                      color="primary"
                      variant="softBg"
                      onClick={updateAddress}
                      className={`w-full gap-2 whitespace-nowrap items-center justify-start text-left h-auto py-1.5 ${currentBroker?.IsPrimePartner ? 'bg-prime hover:bg-black text-white hover:text-white' : 'bg-primary-600'}`}
                    >
                      <MapPin size={16} />
                      Alterar morada de correspondência
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-4 w-full max-w-full mb-4 bg-metal-25">
              <div className="w-full p-4 rounded-lg m-auto">
                <h2
                  className={`text-body-1 text-primary-600 pb-4 ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'}`}
                >
                  Dados da Apólice
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <Table className="pb-10 bg-metal-25">
                    <TableBody>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Frequência
                        </TableHead>
                        <TableCell className="normal-case">
                          {policy.Frequência || 'Frequência indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Forma de Pagamento
                        </TableHead>
                        <TableCell className="normal-case">
                          {policy['Forma de Pagamento'] === 'Direct Debit'
                            ? 'Débito Directo'
                            : policy['Forma de Pagamento'] === 'Aviso de Recibo'
                              ? 'Multibanco'
                              : policy['Forma de Pagamento'] ||
                                'Forma de Pagamento indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Estado
                        </TableHead>
                        <TableCell className="normal-case">
                          {policy.Estado
                            ? renderBadge(policy.Estado)
                            : 'Estado indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Morada de risco
                        </TableHead>
                        <TableCell className="normal-case">
                          {policy.Morada || 'Morada de risco indisponível'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Table className="pb-10 bg-metal-25">
                    <TableBody>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Prémio Anual
                        </TableHead>
                        <TableCell className="normal-case">
                          {policy['Prémio Anual']
                            ? formatCurrency(policy['Prémio Anual'])
                            : 'Prémio Anual indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Data de Início
                        </TableHead>
                        <TableCell className="normal-case">
                          {policy['Data de Inicio']
                            ? policy['Data de Inicio']
                            : 'Data de Início indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Data de Renovação
                        </TableHead>
                        <TableCell className="normal-case">
                          {policy['Data da Renovação']
                            ? policy['Data da Renovação']
                            : 'Data de Renovação indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={2} className="normal-case">
                          <div className="flex flex-wrap items-center gap-4">
                            {buttonArea.map((item, index) => (
                              <div key={`button-${index}`}>
                                <Button
                                  color="primary"
                                  variant="softBg"
                                  className={`w-full gap-2 whitespace-nowrap items-center justify-start text-left h-auto py-1.5 ${currentBroker?.IsPrimePartner ? 'bg-prime hover:bg-black text-white hover:text-white' : 'bg-primary-600'}`}
                                  onClick={item.action}
                                >
                                  {item.icon}
                                  {item.label}
                                </Button>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </Card>

            <Card className="p-4 w-full max-w-full bg-metal-25">
              <div className="w-full p-4 rounded-lg m-auto">
                <h2
                  className={`text-body-1 text-primary-600 pb-4 ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'}`}
                >
                  Coberturas e Franquias
                </h2>
              </div>
            </Card>
          </div>
        )}

        {
          <Modal open={changeAddress} onOpenChange={setChangeAddress}>
            <ModalContent className="max-w-lg p4">
              <h2 className="pb-10 text-xxxl font-medium text-primary-600">
                Mudar morada de cliente
              </h2>
              <form onSubmit={formik.handleSubmit}>
                <FormInput
                  formik={formik}
                  label="Morada"
                  name="ADDRESS1"
                  type="text"
                  required
                />
                <FormInput
                  formik={formik}
                  label="Morada (cont.)"
                  name="ADDRESS2"
                  type="text"
                />
                <FormInput
                  formik={formik}
                  label="Localidade"
                  name="ADDRESS3"
                  type="text"
                  required
                />
                <FormInput
                  formik={formik}
                  label="Código Postal"
                  name="POSTCODE"
                  type="text"
                />
                <FormInput
                  formik={formik}
                  label="País"
                  name="COUNTRY"
                  type="text"
                />
                <h3 className="bg-black/20 rounded-xl p-4 text-primary-500 text-md font-medium mt-4 mb-2">
                  NOTA: A alteração da morada de correspondência será aplicada a
                  todas as moradas associadas a este NIF. A morada de risco
                  permanece inalterada.
                </h3>
                <nav className="flex gap-4 mt-6">
                  <Button
                    variant="outline"
                    color="secondary"
                    className="w-1/2"
                    onClick={() => setChangeAddress(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="w-1/2">
                    Gravar alterações
                  </Button>
                </nav>
              </form>
            </ModalContent>
          </Modal>
        }
      </Wrapper>
    </div>
  )
}
